import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getSessionToken, setSessionToken } from './staffSessionStorage';
import { getAppPassword, setAppPassword } from './appPasswordStorage';
import { SIGNED_OUT_EVENT } from './axiosAuth';
import { StaffAuthContext } from './StaffAuthContext';
import { AppPasswordScreen, AuthMessage, SetPasswordScreen, SignInScreen } from './SignInScreens';
import PortalApp from './portal/PortalApp';
import PublicSite, { sitePageFor } from './site/PublicSite';
import BoxScanPage from './BoxScanPage';

// Blocks the whole app until the backend (middleware/staffAuth.js) lets
// this browser in (issue #251): a staff session, or — while the tenant has
// no staff passwords yet — the shared app password. Also serves the
// /set-password and /reset-password links from invite and reset emails,
// which have to work before anyone is signed in. Sits outside BrowserRouter,
// so nothing below it runs until this passes.
//
// The backend runs on Render, which spins the service down when idle and
// can take tens of seconds to cold-start on the next request — during
// that window a request fails with a network error or a 502, not a 401.
// Only a real 401 means "not signed in"; anything else gets retried.
const MAX_ATTEMPTS = 6;
const RETRY_DELAY_MS = 5000;
const LINK_PATHS = ['/set-password', '/reset-password'];
// The staff sign-in screen's own address, now that / is the public home
// page. Signing in from here lands on the back office at /.
const STAFF_PATH = '/staff';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function checkAccess() {
  try {
    const res = await axios.get('/api/auth/me');
    return { state: 'ok', user: res.data.data?.user || null };
  } catch (err) {
    if (err.response?.status === 401) {
      return { state: err.response.data?.error === 'APP_PASSWORD_REQUIRED' ? 'app_password' : 'sign_in' };
    }
    return { state: 'network' };
  }
}

function linkToken() {
  if (!LINK_PATHS.includes(window.location.pathname)) return null;
  return new URLSearchParams(window.location.search).get('token') || '';
}

function hasStoredStaffAccess() {
  try {
    return !!(getSessionToken() || getAppPassword());
  } catch {
    return false;
  }
}

// A Package Label's link (/b/<token>) is public, like the website.
//
// The Customer portal (/portal/..., issues #253 and #254) is a separate
// kind of login altogether, so it never touches the staff checks below and
// never renders the back office.
//
// The public website (/, /about, /contact, /privacy, /terms, /brands) needs no
// login. / is also the back office's home, so a browser holding a staff
// session (or the app password) goes through the staff check there and
// only falls back to the website if that turns out to be no longer valid —
// everyone else sees the website straight away, without waiting on a
// possibly cold-starting server.
function AuthGate({ children }) {
  const path = window.location.pathname;
  if (/^\/portal(\/|$)/.test(path)) return <PortalApp />;
  // A Package Label's code (issue #300) — opened from any phone camera, by
  // anyone; BoxScanPage sends signed-in staff on into the back office.
  if (/^\/b\/[^/]+\/?$/.test(path)) return <BoxScanPage />;
  if (sitePageFor(path) && (path !== '/' || !hasStoredStaffAccess())) return <PublicSite />;
  return <StaffAuthGate>{children}</StaffAuthGate>;
}

function StaffAuthGate({ children }) {
  const [status, setStatus] = useState(() => (linkToken() !== null ? 'link' : 'checking')); // checking | link | sign_in | app_password | ok
  const [currentUser, setCurrentUser] = useState(null);
  const [waking, setWaking] = useState(false);
  const [notice, setNotice] = useState('');
  // Arrived at / with a stored staff login: if that login is no longer
  // good, show the website rather than the sign-in screen.
  const [siteFallback, setSiteFallback] = useState(() => window.location.pathname === '/');
  const cancelledRef = useRef(false);

  // Resolves to the outcome once a definitive answer (not a network
  // error) comes back, retrying through a cold start.
  const resolveAccess = useCallback(async () => {
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const result = await checkAccess();
      if (cancelledRef.current) return null;
      if (result.state !== 'network') {
        setWaking(false);
        return result;
      }
      if (attempt < MAX_ATTEMPTS - 1) {
        setWaking(true);
        await sleep(RETRY_DELAY_MS);
      }
    }
    setWaking(false);
    return { state: 'network' };
  }, []);

  const applyResult = useCallback((result) => {
    if (!result) return;
    if (result.state === 'ok') {
      if (window.location.pathname === STAFF_PATH) window.history.replaceState(null, '', '/');
      setCurrentUser(result.user);
      setStatus('ok');
    } else if (result.state === 'app_password') {
      setStatus('app_password');
    } else {
      // sign_in, or the server couldn't be reached at all — the sign-in
      // screen reports a connection problem itself on submit.
      setStatus('sign_in');
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    if (status === 'checking') {
      resolveAccess().then(applyResult);
    }
    return () => { cancelledRef.current = true; };
    // Only on mount — later transitions are driven by the handlers below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleSignedOut = () => {
      setSiteFallback(false);
      setCurrentUser(null);
      setNotice('Your session has ended. Sign in again.');
      setStatus('sign_in');
    };
    window.addEventListener(SIGNED_OUT_EVENT, handleSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, handleSignedOut);
  }, []);

  const handleSignedIn = useCallback(({ token, user }) => {
    setSessionToken(token);
    // The shared app password never opens this tenant again once a staff
    // password exists, so there's no reason to keep sending it.
    setAppPassword(null);
    if (LINK_PATHS.includes(window.location.pathname) || window.location.pathname === STAFF_PATH) {
      window.history.replaceState(null, '', '/');
    }
    setNotice('');
    setCurrentUser(user);
    setStatus('ok');
  }, []);

  const goToSignIn = useCallback(() => {
    window.history.replaceState(null, '', STAFF_PATH);
    setSiteFallback(false);
    setStatus('sign_in');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await axios.post('/api/auth/sign-out');
    } catch {
      // Signed out locally either way.
    }
    setSessionToken(null);
    setCurrentUser(null);
    setNotice('');
    window.history.replaceState(null, '', STAFF_PATH);
    setSiteFallback(false);
    setStatus('sign_in');
  }, []);

  const handleAppPassword = useCallback(async (password) => {
    setAppPassword(password);
    const result = await resolveAccess();
    if (result?.state === 'ok') {
      applyResult(result);
      return null;
    }
    if (result?.state === 'sign_in') {
      setAppPassword(null);
      setStatus('sign_in');
      return null;
    }
    if (result?.state === 'network') return 'Could not reach the server. Please try again.';
    setAppPassword(null);
    return 'Incorrect password.';
  }, [resolveAccess, applyResult]);

  const contextValue = useMemo(() => ({ currentUser, signOut }), [currentUser, signOut]);

  if (status === 'link') {
    return <SetPasswordScreen token={linkToken()} onSignedIn={handleSignedIn} onGoToSignIn={goToSignIn} />;
  }
  if (status === 'checking') {
    return <AuthMessage>{waking ? 'Connecting to the server. It may be starting up, which can take up to a minute...' : ''}</AuthMessage>;
  }
  if (siteFallback && (status === 'sign_in' || status === 'app_password')) {
    return <PublicSite />;
  }
  if (status === 'app_password') {
    return <AppPasswordScreen onSubmit={handleAppPassword} />;
  }
  if (status === 'sign_in') {
    return <SignInScreen notice={notice} onSignedIn={handleSignedIn} />;
  }

  return (
    <StaffAuthContext.Provider value={contextValue}>
      {children}
    </StaffAuthContext.Provider>
  );
}

export default AuthGate;
